// LexicalShowcase.jsx — PDF JSON Bound Lexical Editor
import React, { useCallback, useEffect, useState, useRef } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes } from "@lexical/html";
import PdfPasteFixPlugin from "./PdfPasteFixPlugin";
import { LineBreakPlugin } from "./LineBreakPlugin";
import { useReportTemplateContext } from "../../ReportTemplate/ReportTemplateContext";
import { generateReportSection, previewReportSection } from "../../../../LLM/OpenAI/openAiApi.js";
import PromptPreviewModal from "../../../llmPrompts/PromptPreviewModal";
import { useUser } from "../../../../context/UserContext.jsx";
import { can } from "../../../../permissions/access.js";
import { PERMISSIONS } from "../../../../permissions/permissions.js";
import { useParams } from "react-router-dom";
import PromptTagSelect from "../../../llmPrompts/PromptTagSelect";

import {
  TextNode,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND, REDO_COMMAND,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createTextNode,
  $createParagraphNode,
  $createLineBreakNode,
  LineBreakNode
} from "lexical";

import {
  ListNode,
  ListItemNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_CHECK_LIST_COMMAND,
  registerCheckList,
} from "@lexical/list";

import {
  TableNode,
  TableCellNode,
  TableRowNode,
  INSERT_TABLE_COMMAND,
  registerTablePlugin,
  registerTableSelectionObserver,
  $getTableCellNodeFromLexicalNode,
  $insertTableRowAtSelection,
  $insertTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $deleteTableColumnAtSelection,
} from "@lexical/table";

import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { $patchStyleText } from "@lexical/selection";
import "./lexicalStyles.css";
import TextRecommendationBanner from "./TextRecommendationBanner";

import {
  CONDITION_PARAMS,
  extractParamValue, evalCondition,
  getTagsCache, setTagsCache, getTagIdByNimi,
} from '../../../../utils/valmislauseStorage';
import {
  getValmislauseTagit, createValmislauseTag, deleteValmislauseTag, setValmislauseTagReportTypes,
  setValmislauseTagSections,
  getValmislauseet,
  getLlmReportTypes,
} from '../../../../api/api';
import ValmislauseBrowser from '../../../ValmislauseBrowser.jsx';

// A saved valmislause's teksti can hold {{param_key}} placeholders (CONDITION_PARAMS
// keys) — written once, resolved against whichever report it's actually inserted
// into, so the sentence stays correct when reused across different properties
// instead of baking in the building year/address of whichever report it was
// first written for. No value to resolve to -> a visible [Label] placeholder,
// never a silently blank or "null" spot in the text.
function resolveMuuttujat(teksti, rakennusData, mergedRakennusData) {
  return teksti.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const def = CONDITION_PARAMS.find(p => p.key === key);
    if (!def) return match;
    const val = extractParamValue(key, rakennusData, mergedRakennusData);
    return val != null ? String(val) : `[${def.label}]`;
  });
}


/* ============================================================
   ValmislauseModal — browse any tag's valmislauseet, insert one, create
   new ones and set/edit their condition. Opened from the "+ Valmislause"
   trigger below the textarea. Deleting a valmislause entirely still only
   happens on the Tekstisuositukset admin dashboard.
   ============================================================ */
function ValmislauseModal({ tags, allTags, allReportTypes, reportType, sectionType, onAddTag, onRemoveTag, onCreateTag, onDeleteTag, onTagReportTypesChange, onTagSectionsChange, onInsert, onClose }) {
  const { rakennusData, mergedRakennusData } = useReportTemplateContext();
  const [headerTag, setHeaderTag] = useState(tags[0] ?? null);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      // Below Bootstrap's own .modal-backdrop/.modal (z-index 1050/1055) —
      // confirmAndRun's delete-tag confirmation is a real Bootstrap modal,
      // and it needs to render above this one, not behind it.
      style={{ position: 'fixed', inset: 0, background: 'rgba(33,37,41,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="shadow-lg"
        // maxHeight (not a fixed height) + overflowY: the card grows to fit
        // its content and only becomes a scroll container once content
        // actually exceeds 95vh — ONE real scrollable element, instead of
        // the previous fixed-height card forcing several nested panels to
        // each separately (and unreliably) manage their own overflow.
        style={{ width: 'min(1480px, 97vw)', maxHeight: '95vh', background: '#fff', borderRadius: 14, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
      >
        <div className="d-flex align-items-center gap-2" style={{
          padding: '14px 18px 10px', borderBottom: '1px solid #dee2e6',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, flex: 1 }}>
            Valmislause{headerTag ? <> — <span style={{ color: '#198754' }}>{headerTag}</span></> : null}
          </div>
          <button onClick={onClose} title="Sulje" className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
            style={{ width: 28, height: 28, padding: 0, borderRadius: 7 }}>✕</button>
        </div>

        <ValmislauseBrowser
          typeTabRow="single"
          reportType={reportType}
          sectionType={sectionType}
          tags={tags}
          onAddTag={onAddTag}
          onRemoveTag={onRemoveTag}
          onInsert={text => onInsert(resolveMuuttujat(text, rakennusData, mergedRakennusData))}
          onCreateTag={onCreateTag}
          onDeleteTag={onDeleteTag}
          onTagReportTypesChange={onTagReportTypesChange}
          onTagSectionsChange={onTagSectionsChange}
          allTags={allTags}
          allReportTypes={allReportTypes}
          rakennusData={rakennusData}
          mergedRakennusData={mergedRakennusData}
          onSelectedTagChange={setHeaderTag}
          onDone={onClose}
        />
      </div>
    </div>
  );
}

/* ============================================================
   ValmislauseInlineList — compact, read-only list of a linked tag's
   valmislauseet shown right under the chip row, so the sentences are
   visible without opening the modal. Browsing, creating, deleting tags
   and editing conditions stays in ValmislauseModal only; this is just
   "here's what's available, click to use".

   A tag's options are alternatives, not independent inserts — picking a
   different one swaps out whichever one from this tag was inserted last;
   clicking the same one again removes it. `onChoose` (owned by the
   parent, which tracks the one active choice per tag) does the actual
   remove+insert. No visual "which one is active" indicator here — it
   can't be kept reliably in sync with the editor's real content (e.g.
   after a manual edit), so showing one was worse than showing none.
   ============================================================ */
function ValmislauseInlineList({ tag, onChoose }) {
  const { rakennusData, mergedRakennusData } = useReportTemplateContext();
  const [list, setList] = useState([]);

  const load = useCallback(() => {
    const cached = getTagIdByNimi(tag);
    if (cached) { getValmislauseet(cached).then(r => setList(r.data ?? [])).catch(() => setList([])); return; }
    getValmislauseTagit().then(r => {
      const tags = r.data ?? [];
      setTagsCache(tags);
      const found = tags.find(t => t.nimi === tag);
      if (found) getValmislauseet(found.id).then(r2 => setList(r2.data ?? [])).catch(() => setList([]));
      else setList([]);
    });
  }, [tag]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    window.addEventListener('valmislause-updated', load);
    return () => window.removeEventListener('valmislause-updated', load);
  }, [load]);

  if (list.length === 0) return null;

  return (
    <div className="mb-2">
      <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', color: '#198754', marginBottom: 3, textTransform: 'uppercase' }}>
        {tag}
      </div>
      <div className="d-flex flex-column gap-1">
      {list.map(t => {
        const condResult = t.condition ? evalCondition(t.condition, rakennusData, mergedRakennusData) : null;
        const isMatch = condResult === true;
        const currentVal = t.condition ? extractParamValue(t.condition.param, rakennusData, mergedRakennusData) : null;
        const paramDef = t.condition ? CONDITION_PARAMS.find(d => d.key === t.condition.param) : null;
        const restBg = isMatch || !t.condition ? '#f0fff4' : '#fff';
        const hoverBg = isMatch || !t.condition ? '#dcf5e4' : '#f1f3f4';
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChoose(t)}
            title={t.teksti}
            className="d-flex align-items-center gap-2 rounded border text-start w-100"
            onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
            onMouseLeave={e => { e.currentTarget.style.background = restBg; }}
            style={{
              padding: '4px 4px 4px 10px', fontSize: '0.75rem', cursor: 'pointer',
              borderColor: isMatch || !t.condition ? '#b8ddb8' : '#dee2e6',
              background: restBg,
            }}
          >
            {t.condition && (
              <span title={`JOS ${paramDef?.label ?? t.condition.param} ${t.condition.operator} ${t.condition.value}`} style={{
                fontSize: '0.62rem', fontWeight: 700, padding: '1px 6px', borderRadius: 999, flexShrink: 0,
                background: isMatch ? '#198754' : '#f1f3f4', color: isMatch ? '#fff' : '#6c757d',
              }}>
                {isMatch ? `⚡ ${currentVal}` : 'ehdollinen'}
              </span>
            )}
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isMatch || !t.condition ? '#212529' : '#adb5bd' }}>
              {t.title || t.teksti}
            </span>
          </button>
        );
      })}
      </div>
    </div>
  );
}

const theme = {
  text: {
    bold: "fw-bold",
    italic: "fst-italic",
    underline: "text-decoration-underline",
  },
  list: {
    checklist: "lx-checklist",
    listitemChecked: "lx-listitem-checked",
    listitemUnchecked: "lx-listitem-unchecked",
  },
  table: "lx-table",
  tableCell: "lx-table-cell",
  tableCellHeader: "lx-table-cell-header",
  tableSelected: "lx-table-selected",
};

/* ---------------- Table Plugin ----------------- */
function TablePlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    const unregPlugin = registerTablePlugin(editor);
    const unregObserver = registerTableSelectionObserver(editor, true);
    return () => { unregPlugin(); unregObserver(); };
  }, [editor]);
  return null;
}

/* ---------------- Table Grid Picker ----------------- */
const HEADER_OPTIONS = [
  { key: 'row',  label: 'Ylärivi',  headers: { rows: true,  columns: false } },
  { key: 'col',  label: 'Vasensar', headers: { rows: false, columns: true  } },
  { key: 'both', label: 'Molemmat', headers: { rows: true,  columns: true  } },
  { key: 'none', label: 'Ei otsik', headers: false },
];

function TableGridPicker({ onSelect, onClose }) {
  const [hover, setHover] = React.useState([0, 0]);
  const [headerKey, setHeaderKey] = React.useState('row');
  const MAX = 8;
  const hdrOpt = HEADER_OPTIONS.find(o => o.key === headerKey);

  const pillBtn = (active) => ({
    fontSize: 10, padding: '2px 6px', borderRadius: 10, border: '1px solid',
    borderColor: active ? '#04aa00' : '#dee2e6',
    background: active ? '#04aa00' : '#f8f9fa',
    color: active ? '#fff' : '#495057',
    cursor: 'pointer', lineHeight: 1.4,
  });

  return (
    <div
      style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, background: '#fff', border: '1px solid #dee2e6', borderRadius: 6, padding: 6, boxShadow: '0 4px 12px rgba(0,0,0,.15)', minWidth: 148 }}
      onMouseLeave={() => setHover([0, 0])}
    >
      {/* Header style selector */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 6 }}>
        {HEADER_OPTIONS.map(o => (
          <button key={o.key} style={pillBtn(headerKey === o.key)} onClick={() => setHeaderKey(o.key)}>
            {o.label}
          </button>
        ))}
      </div>

      {/* Size grid */}
      {Array.from({ length: MAX }, (_, r) => (
        <div key={r} style={{ display: 'flex' }}>
          {Array.from({ length: MAX }, (_, c) => {
            const inRange = r <= hover[0] && c <= hover[1];
            const isHdrRow = hdrOpt.headers !== false && hdrOpt.headers.rows && r === 0;
            const isHdrCol = hdrOpt.headers !== false && hdrOpt.headers.columns && c === 0;
            const isHeader = inRange && (isHdrRow || isHdrCol);
            return (
              <div
                key={c}
                onMouseEnter={() => setHover([r, c])}
                onClick={() => { onSelect(r + 1, c + 1, hdrOpt.headers); onClose(); }}
                style={{
                  width: 15, height: 15, margin: 1, border: '1px solid', borderRadius: 1, cursor: 'pointer',
                  borderColor: inRange ? '#04aa00' : '#dee2e6',
                  background: isHeader ? '#04aa00' : inRange ? '#d1e7dd' : '#f8f9fa',
                }}
              />
            );
          })}
        </div>
      ))}
      <div style={{ fontSize: 10, textAlign: 'center', marginTop: 3, color: '#6c757d' }}>
        {hover[0] + 1} × {hover[1] + 1}
      </div>
    </div>
  );
}

/* ---------------- Toolbar ----------------- */
function Toolbar({ usePastePlugin, setUsePastePlugin, onGenerateClick, generating, showGenerate, promptTagControl }) {
  const [editor] = useLexicalComposerContext();

  const colors = [
    { name: "Black", value: "#000000" },
    { name: "White", value: "#ffffff" },
    { name: "Success", value: "var(--bs-success)" },
    { name: "Warning", value: "var(--bs-warning)" },
    { name: "Danger", value: "var(--bs-danger)" },
  ];

  const highlightColors = [
    { name: "Success", value: "var(--bs-success)" },
    { name: "Warning", value: "var(--bs-warning)" },
    { name: "Danger", value: "var(--bs-danger)" },
  ];

  const [activeFormats, setActiveFormats] = React.useState({
    bold: false,
    italic: false,
    underline: false,
    align: null,
    list: null,
    color: null,
    highlight: null,
  });
  const [inTable, setInTable] = React.useState(false);
  const [showTablePicker, setShowTablePicker] = React.useState(false);
  const tablePickerRef = React.useRef(null);

  const resolveColor = (color) => {
    if (color.startsWith("var(")) {
      const varName = color.slice(4, -1).trim();
      return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || varName;
    }
    return color;
  };

  const applyStyle = (styleKey, color) => {
    const resolvedColor = resolveColor(color);
    editor.update(() => {
      const selection = $getSelection();
      if (!selection) return;
      $patchStyleText(selection, { [styleKey]: resolvedColor });
    });
  };

  const applyColor = (color) => applyStyle("color", color);
  const applyHighlight = (color) => applyStyle("background-color", color);

  const ALIGN_MAP = { left: 1, center: 2, right: 3, justify: 4 };

  React.useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!selection) return;

        let bold = false, italic = false, underline = false,
          align = null, list = null, color = null, highlight = null;

        const nodes = selection.getNodes?.() || [];
        nodes.forEach((node) => {
          if (node.__text) {
            const styles = node.getStyle() || "";
            if (node.hasFormat?.("bold")) bold = true;
            if (node.hasFormat?.("italic")) italic = true;
            if (node.hasFormat?.("underline")) underline = true;
            const styleObj = styles.split(";").reduce((acc, s) => {
              const [k, v] = s.split(":").map((str) => str?.trim());
              if (k && v) acc[k] = v;
              return acc;
            }, {});
            if (styleObj.color) color = styleObj.color;
            if (styleObj["background-color"]) highlight = styleObj["background-color"];
          }
          if (node.getListType) {
            const listType = node.getListType();
            if (listType) list = listType;
          }
          if (node.getParent) {
            let parent = node.getParent();
            while (parent) {
              if (parent.getFormat) {
                const fmt = parent.getFormat();
                if (fmt) { align = fmt; break; }
              }
              parent = parent.getParent?.();
            }
          }
        });

        setActiveFormats({ bold, italic, underline, align, list, color, highlight });

        // Detect if selection is inside a table cell
        if ($isRangeSelection(selection)) {
          const anchor = selection.anchor.getNode();
          setInTable($getTableCellNodeFromLexicalNode(anchor) !== null);
        } else {
          setInTable(false);
        }
      });
    });
  }, [editor]);

  // Close table picker on outside click
  React.useEffect(() => {
    if (!showTablePicker) return;
    const handler = (e) => { if (!tablePickerRef.current?.contains(e.target)) setShowTablePicker(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showTablePicker]);

  const divider = <span style={{ width: 1, height: 20, background: "#dee2e6", display: "inline-block", alignSelf: "center" }} />;
  const tbBtn = (active) => ({
    background: active ? "#e9ecef" : "transparent",
    border: "none",
    borderRadius: 4,
    padding: "3px 7px",
    cursor: "pointer",
    lineHeight: 1,
    fontSize: 13,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
  });

  return (
    <div
      className="mb-2 border-bottom pb-2"
      style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 10, boxShadow: "0 2px 5px rgba(0,0,0,0.1)" }}
    >
      <div className="d-flex flex-wrap align-items-center gap-1 pt-2 px-2" style={{ rowGap: "6px" }}>

        {/* Text style */}
        <div className="d-flex gap-1">
          <button style={tbBtn(false)} title="Normal text"
            onClick={() => editor.update(() => {
              const sel = $getSelection();
              if (sel) sel.getNodes().forEach(n => { if (n.__text) { n.setFormat(0); n.setStyle(""); } });
            })}>
            A
          </button>
          <button style={tbBtn(activeFormats.bold)} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}><b>B</b></button>
          <button style={tbBtn(activeFormats.italic)} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}><i>I</i></button>
          <button style={tbBtn(activeFormats.underline)} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}><u>U</u></button>
        </div>

        {divider}

        {/* Lists */}
        <div className="d-flex gap-1">
          <button style={tbBtn(activeFormats.list === "number")} onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND)}>1.</button>
          <button style={tbBtn(activeFormats.list === "bullet")} onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND)}>•</button>
          <button style={tbBtn(activeFormats.list === "check")} onClick={() => editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND)}>✓</button>
        </div>

        {divider}

        {/* Text color */}
        <div className="d-flex align-items-center gap-1">
          <span style={{ fontSize: 11, color: "#6c757d", lineHeight: 1 }}>A</span>
          {colors.map((c) => (
            <button key={c.name}
              title={`Tekstin väri: ${c.name}`}
              onClick={() => applyColor(c.value)}
              style={{
                width: 18, height: 18, padding: 0, border: "none", borderRadius: 3, cursor: "pointer",
                backgroundColor: c.value,
                outline: activeFormats.color === resolveColor(c.value) ? "2px solid #333" : "1px solid #ccc",
                outlineOffset: 1,
              }} />
          ))}
        </div>

        {divider}

        {/* Highlight */}
        <div className="d-flex align-items-center gap-1">
          <i className="bi bi-highlighter" style={{ fontSize: 12, color: "#6c757d" }} />
          {highlightColors.map((c) => (
            <button key={c.name}
              title={`Korostus: ${c.name}`}
              onClick={() => applyHighlight(c.value)}
              style={{
                width: 18, height: 18, padding: 0, border: "none", borderRadius: 3, cursor: "pointer",
                backgroundColor: c.value,
                outline: activeFormats.highlight === resolveColor(c.value) ? "2px solid #333" : "1px solid #999",
                outlineOffset: 1,
              }} />
          ))}
        </div>

        {divider}

        {/* Paste toggle */}
        <div className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: "#6c757d" }}>
          <input type="checkbox" id="pastePluginToggle" className="form-check-input mt-0"
            checked={usePastePlugin} onChange={(e) => setUsePastePlugin(e.target.checked)}
            style={{ width: 14, height: 14 }} />
          <label htmlFor="pastePluginToggle" style={{ cursor: "pointer", userSelect: "none" }}>
            <span className="d-none d-sm-inline">Liitä formatoimatta</span>
            <span className="d-inline d-sm-none">Plain</span>
          </label>
        </div>

        {divider}

        {/* Undo / Redo */}
        <div className="d-flex gap-1">
          <button style={tbBtn(false)} onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)} title="Kumoa">
            <i className="bi bi-arrow-counterclockwise" style={{ fontSize: 13 }} />
          </button>
          <button style={tbBtn(false)} onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)} title="Tee uudelleen">
            <i className="bi bi-arrow-clockwise" style={{ fontSize: 13 }} />
          </button>
        </div>

        {divider}

        {/* Table insert + edit controls */}
        <div className="d-flex align-items-center gap-1" style={{ position: 'relative' }} ref={tablePickerRef}>
          <button
            style={tbBtn(false)}
            title="Lisää taulukko"
            onClick={() => setShowTablePicker(v => !v)}
          >
            <i className="bi bi-table" style={{ fontSize: 13 }} />
          </button>
          {showTablePicker && (
            <TableGridPicker
              onSelect={(rows, cols) => {
                editor.dispatchCommand(INSERT_TABLE_COMMAND, { rows: String(rows), columns: String(cols), includeHeaders: true });
              }}
              onClose={() => setShowTablePicker(false)}
            />
          )}
        </div>

        {/* Table row/column actions — only visible when cursor is in a table */}
        {inTable && (
          <>
            {divider}
            <div className="lx-table-toolbar">
              <button title="Lisää rivi alle" onClick={() => editor.update(() => $insertTableRowAtSelection(true))}>+Rivi↓</button>
              <button title="Lisää rivi yli" onClick={() => editor.update(() => $insertTableRowAtSelection(false))}>+Rivi↑</button>
              <button title="Lisää sarake oikealle" onClick={() => editor.update(() => $insertTableColumnAtSelection(true))}>+Sar→</button>
              <button title="Lisää sarake vasemmalle" onClick={() => editor.update(() => $insertTableColumnAtSelection(false))}>+Sar←</button>
              <button className="danger" title="Poista rivi" onClick={() => editor.update(() => $deleteTableRowAtSelection())}>−Rivi</button>
              <button className="danger" title="Poista sarake" onClick={() => editor.update(() => $deleteTableColumnAtSelection())}>−Sar</button>
            </div>
          </>
        )}

        {/* LLM prompt tag + AI Generate — the button only exists when a prompt is tagged */}
        {(promptTagControl || showGenerate) && (
          <>
            {divider}
            {promptTagControl}
            {showGenerate && <button
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: 12, padding: "2px 8px", height: 28 }}
              onClick={onGenerateClick}
              disabled={generating}
            >
              {generating
                ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10 }} />Generoidaan...</>
                : "✨ Generoi"
              }
            </button>}
          </>
        )}

      </div>
    </div>
  );
}

/* ---------------- HydrateFromData ----------------- */

function CheckListRegistrar() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => registerCheckList(editor), [editor]);
  return null;
}

function HydrateFromData({ data }) {
  const [editor] = useLexicalComposerContext();
  const hydratedRef = useRef(false);

  const domNodeToLexicalNodes = (domNode, parentFormats = {}, parentStyles = {}) => {
    if (domNode.nodeType === Node.TEXT_NODE) {
      const textContent = domNode.textContent;
      if (!textContent) return [];
      const newNode = $createTextNode(textContent);
      const styleObj = { ...parentStyles };
      if (Object.keys(styleObj).length) {
        newNode.setStyle(Object.entries(styleObj).map(([k, v]) => `${k}: ${v}`).join("; "));
      }
      if (parentFormats.bold) newNode.toggleFormat("bold");
      if (parentFormats.italic) newNode.toggleFormat("italic");
      if (parentFormats.underline) newNode.toggleFormat("underline");
      return [newNode];
    }

    if (domNode.nodeType !== Node.ELEMENT_NODE) return [];

    const newFormats = { ...parentFormats };
    const newStyles = { ...parentStyles };
    const tag = domNode.tagName;

    if (tag === "BR") return [$createLineBreakNode()];
    if (tag === "B" || tag === "STRONG") newFormats.bold = true;
    if (tag === "I" || tag === "EM") newFormats.italic = true;
    if (tag === "U") newFormats.underline = true;

    const style = domNode.style;
    if (style.color) newStyles.color = style.color;
    if (style.backgroundColor) newStyles["background-color"] = style.backgroundColor;

    const children = Array.from(domNode.childNodes).flatMap((child) =>
      domNodeToLexicalNodes(child, newFormats, newStyles)
    );

    if (tag === "P") {
      const paragraph = $createParagraphNode();
      paragraph.append(...children);
      paragraph.setFormat("justify");
      return [paragraph];
    }

    if (tag === "SPAN") {
      children.forEach((child) => { if (child.setFormat) child.setFormat("justify"); });
      return children;
    }

    if (tag === "LI") {
      const liNode = new ListItemNode();
      liNode.append(...children);
      return [liNode];
    }

    if (tag === "UL" || tag === "OL") {
      const isCheckList = tag === "UL" && Array.from(domNode.childNodes).some(
        n => n.nodeType === Node.ELEMENT_NODE && n.getAttribute?.('role') === 'checkbox'
      );
      const listType = tag === "OL" ? "number" : isCheckList ? "check" : "bullet";
      const listNode = new ListNode(listType);

      if (isCheckList) {
        Array.from(domNode.childNodes).forEach(child => {
          if (child.nodeType !== Node.ELEMENT_NODE || child.tagName !== 'LI') return;
          const liNode = new ListItemNode();
          liNode.setChecked(false);
          const innerNodes = Array.from(child.childNodes).flatMap(n =>
            domNodeToLexicalNodes(n, newFormats, newStyles)
          );
          if (innerNodes.length) liNode.append(...innerNodes);
          listNode.append(liNode);
        });
      } else {
        children.forEach((child) => {
          if (child instanceof ListItemNode) listNode.append(child);
          else { const li = new ListItemNode(); li.append(child); listNode.append(li); }
        });
      }
      return [listNode];
    }

    return children;
  };

  useEffect(() => {
    if (hydratedRef.current) return;
    if (!data?.html && !data?.value && !data?.lexicalState) return;
    hydratedRef.current = true;

    // Prefer JSON state for round-trip fidelity (preserves tables and all node types)
    if (data?.lexicalState) {
      try {
        const parsed = editor.parseEditorState(
          typeof data.lexicalState === 'string' ? data.lexicalState : JSON.stringify(data.lexicalState)
        );
        editor.setEditorState(parsed);
        return;
      } catch (e) {
        console.warn('Failed to restore lexicalState, falling back to HTML', e);
      }
    }

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      if (data?.html) {
        const parser = new DOMParser();
        const dom = parser.parseFromString(data.html, "text/html");
        const nodes = Array.from(dom.body.childNodes).flatMap(domNodeToLexicalNodes);
        root.append(...nodes);
      } else {
        // Plain text — split on double newlines to create paragraphs
        const paragraphs = (data.value || "").split(/\n\n+/);
        paragraphs.forEach((paraText) => {
          const paragraph = $createParagraphNode();
          paragraph.setFormat("justify");
          // Handle single newlines within a paragraph
          const lines = paraText.split("\n");
          lines.forEach((line, idx) => {
            paragraph.append($createTextNode(line));
            if (idx < lines.length - 1) paragraph.append($createLineBreakNode());
          });
          root.append(paragraph);
        });
      }
    });
  }, [editor, data?.html, data?.value]);

  return null;
}

/* ---------------- InjectContentPlugin ----------------- */
function InjectContentPlugin({ content, removeContent, mode = 'replace', onDone }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!content && !removeContent) return;
    editor.update(() => {
      const root = $getRoot();
      // Toggling a valmislause choice off (or switching to a different one
      // from the same tag) removes its exact previously-inserted paragraph
      // first — matched by exact text, since that's all a plain paragraph
      // node carries — before any new content goes in.
      if (removeContent) {
        const target = removeContent.trim();
        root.getChildren().forEach(node => {
          if (node.getTextContent().trim() === target) node.remove();
        });
      }
      if (content) {
        // 'append' (the text-recommendation banner's "Lisää", and every
        // valmislause "Käytä") keeps whatever is already there and adds the
        // new paragraphs after it; 'replace' (the original AI-generate
        // "Käytä" / the banner's own "Käytä") clears first, unchanged from
        // before. An editor that's never had anything typed into it still
        // holds Lexical's own default single empty paragraph — appending
        // after THAT (rather than clearing it) left a blank leading
        // paragraph before the very first inserted sentence, so an "empty"
        // root is cleared even in append mode: there's nothing meaningful
        // to preserve a gap for. Checked AFTER any removal above, so
        // removing the last remaining paragraph correctly falls back to a
        // clean insert too.
        if (mode !== 'append' || root.getTextContent().trim() === '') root.clear();
        const paragraphs = content.split(/\n\n+/);
        paragraphs.forEach((paraText) => {
          const paragraph = $createParagraphNode();
          paragraph.setFormat("justify");
          const lines = paraText.split("\n");
          lines.forEach((line, idx) => {
            paragraph.append($createTextNode(line));
            if (idx < lines.length - 1) paragraph.append($createLineBreakNode());
          });
          root.append(paragraph);
        });
      }
    });
    onDone?.();
  }, [content, removeContent]);
  return null;
}

/* ---------------- Table caption input (local state, saves on blur) ----------------- */
function CaptionInput({ value, onBlur }) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <input
      type="text"
      className="form-control form-control-sm"
      style={{ fontSize: '0.78rem' }}
      placeholder="Taulukon kuvaus (näkyy PDF:ssä)..."
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => onBlur(local)}
    />
  );
}

/* ---------------- Main Editor ----------------- */
// Plain free-text note to the writer — not included in PDF. Tag linking
// lives in ValmislauseTagsPicker below, on its own row — kept separate
// from this field (rather than the original single-row layout) since the
// user specifically wants the chip picker visible on its own line under
// the hint, not folded back into one row with it.
function HintInput({ hint, onBlur }) {
  const [local, setLocal] = useState(hint);

  return (
    <input
      type="text"
      className="form-control form-control-sm border-0 border-bottom rounded-0 bg-transparent mb-1"
      style={{ fontSize: '0.88rem', fontStyle: 'italic', fontWeight: 600, color: '#ba3b46' }}
      placeholder="Ohje kirjoittajalle (ei näy PDF:ssä)..."
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { onBlur(local); }}
    />
  );
}

// Explicit valmislause-tag linking: removable chips for the tags already
// linked to this block (data.valmislauseTags, its own field — not parsed
// out of hint text), plus a "+ Valmislause-tagi" picker to add more or
// create a new tag. Type filter is a chip row (not a <select>) with the
// current report's own type pinned first — that's the pool you want most
// of the time — and each tag row shows a dot per report type it's scoped
// to (grey = universal) so it's visible at a glance why a tag is or isn't
// showing under the active filter.
function ValmislauseTagsPicker({ tags, onChange, reportType, sectionType, onInsert }) {
  const [showModal, setShowModal] = useState(false);
  const [allTags, setAllTags] = useState(() => getTagsCache());
  const [allReportTypes, setAllReportTypes] = useState([]);

  // Fetched on mount (not just when the modal opens) so allTags is available
  // to filter the chip row below — a tag deleted elsewhere (e.g. the admin
  // page) must stop showing as a chip here even if this block's own modal
  // is never opened again.
  useEffect(() => {
    getValmislauseTagit().then(r => {
      const t = r.data ?? [];
      setTagsCache(t);
      setAllTags(t);
    });
  }, []);

  useEffect(() => {
    if (!showModal) return;
    getValmislauseTagit().then(r => {
      const t = r.data ?? [];
      setTagsCache(t);
      setAllTags(t);
    });
    getLlmReportTypes().then(r => setAllReportTypes(r.data ?? [])).catch(() => setAllReportTypes([]));
  }, [showModal]);

  // Tags this block links to by name, but with any since-deleted tag
  // dropped — allTags is empty only before the mount fetch above resolves,
  // in which case show everything rather than flash them away.
  const visibleTags = allTags.length > 0 ? tags.filter(nimi => allTags.some(t => t.nimi === nimi)) : tags;

  const addTag = (nimi) => {
    if (!tags.includes(nimi)) onChange([...tags, nimi]);
  };
  const removeTag = (nimi) => onChange(tags.filter(t => t !== nimi));

  // reportTypes: [] means universal (Yleiset); [reportType] scopes the new
  // tag to whichever tab was active when it was created — previously it
  // was always created universal regardless of the active tab. sections:
  // set when creating from a chapter's own "+" button, so the tag starts
  // scoped to that chapter instead of needing a second trip through
  // ChapterScopePicker.
  const createTag = async (name, reportTypes = [], sections = []) => {
    const existing = allTags.find(x => x.nimi === name);
    if (existing) return existing;
    try {
      const res = await createValmislauseTag(name);
      let tag = res.data;
      if (reportTypes.length) {
        await setValmislauseTagReportTypes(tag.id, reportTypes);
        tag = { ...tag, report_types: reportTypes };
      }
      if (sections.length) {
        await setValmislauseTagSections(tag.id, sections);
        tag = { ...tag, sections };
      }
      const updated = [...allTags, tag].sort((a, b) => a.nimi.localeCompare(b.nimi));
      setTagsCache(updated);
      setAllTags(updated);
      return tag;
    } catch {
      return null;
    }
  };

  const deleteTag = async (id) => {
    await deleteValmislauseTag(id);
    const updated = allTags.filter(t => t.id !== id);
    setTagsCache(updated);
    setAllTags(updated);
  };

  const updateTagReportTypes = (id, reportTypes) => {
    const updated = allTags.map(t => t.id === id ? { ...t, report_types: reportTypes } : t);
    setTagsCache(updated);
    setAllTags(updated);
  };

  const updateTagSections = (id, sections) => {
    const updated = allTags.map(t => t.id === id ? { ...t, sections } : t);
    setTagsCache(updated);
    setAllTags(updated);
  };

  return (
    <div className="d-flex align-items-center gap-1 flex-wrap mb-2">
      {visibleTags.map(nimi => (
        <span key={nimi} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: '0.72rem', fontWeight: 600, color: '#198754',
          background: '#eafbf1', border: '1px solid #b8ddb8', borderRadius: 999,
          padding: '2px 8px 2px 10px',
        }}>
          {nimi}
          <button type="button" onClick={() => removeTag(nimi)} title="Poista linkitys"
            style={{ border: 'none', background: 'none', color: '#198754', cursor: 'pointer', fontSize: '0.78rem', lineHeight: 1, padding: 0 }}>
            ×
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={() => setShowModal(true)}
        style={{
          fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999,
          border: '1px dashed #adb5bd',
          background: '#fff', color: '#6c757d',
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        + Valmislause
      </button>

      {showModal && (
        <ValmislauseModal
          tags={tags}
          allTags={allTags}
          allReportTypes={allReportTypes}
          reportType={reportType}
          sectionType={sectionType}
          onAddTag={addTag}
          onRemoveTag={removeTag}
          onCreateTag={createTag}
          onDeleteTag={deleteTag}
          onTagReportTypesChange={updateTagReportTypes}
          onTagSectionsChange={updateTagSections}
          onInsert={onInsert}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default function LexicalShowcase({ data, onChange, subsectionTitle, blockPathLabel, tableStartNumber, blockId }) {
  const { extractMeaningfulReportData, activeSection, report, rakennusData, mergedRakennusData } = useReportTemplateContext();
  const { localUser } = useUser();
  // Hidden before this push — not ready to show on dev yet, even to users
  // who already have AI_GENERATE. Hides the prompt-tag dropdown, "Generoi"
  // button, confirm dialog, and generated-text preview all at once, since
  // every one of them is gated on this single flag. Remove the "false &&"
  // to bring it back.
  const canAiGenerate = false && can(localUser, PERMISSIONS.REPORTS.AI_GENERATE);
  const { templateName } = useParams();
  // LLM prompt tagged on this text area (set next to the Generoi button, saved in the
  // block data so templates pass it on to new reports).
  const promptKey = data?.llmPrompt || null;
  const [generating, setGenerating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [generatedText, setGeneratedText] = useState(null);
  const [injectContent, setInjectContent] = useState(null);
  const [removeInjectContent, setRemoveInjectContent] = useState(null);
  const [injectMode, setInjectMode] = useState('replace');
  const [usePastePlugin, setUsePastePlugin] = useState(true);
  const [plain, setPlain] = useState(data?.value || "");
  const [htmlDebug, setHtmlDebug] = useState("");
  const [composerKey] = useState(() => data?.lexicalId || crypto.randomUUID());
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  const ensureLexicalId = (data) => {
    if (!data.lexicalId) data.lexicalId = crypto.randomUUID();
    return data.lexicalId;
  };
  ensureLexicalId(data);

  const initialConfig = {
    namespace: "lexical-showcase",
    theme,
    onError(error) { console.error(error); },
    nodes: [TextNode, LineBreakNode, HeadingNode, QuoteNode, ListNode, ListItemNode, TableNode, TableCellNode, TableRowNode],
  };

  const handleChange = useCallback(
    (editorState, editor) => {
      editorState.read(() => {
        const text = $getRoot().getTextContent();
        const html = $generateHtmlFromNodes(editor);
        setPlain(text);
        setHtmlDebug(html);
        onChange?.({ ...dataRef.current, value: text, html, lexicalState: editorState.toJSON() });
      });
    },
    [onChange]
  );

  // "Näytä koko prompti": the exact messages generation would send, without generating.
  const [preview, setPreview] = useState({ show: false, loading: false, error: null, data: null });
  const generationParams = async () => ({
    newReportContext: await extractMeaningfulReportData(),
    targetSection: activeSection?.type,
    promptKey,
    reportType: templateName,
    currentText: plain,
    instructions: subsectionTitle ? `Kirjoita teksti alaosiolle: "${subsectionTitle}"` : undefined,
  });
  const handleShowPrompt = async () => {
    setPreview({ show: true, loading: true, error: null, data: null });
    try {
      const data = await previewReportSection(await generationParams());
      setPreview({ show: true, loading: false, error: null, data });
    } catch (err) {
      setPreview({ show: true, loading: false, error: err?.response?.data?.error || 'Promptin muodostus epäonnistui.', data: null });
    }
  };

  const handleAiGenerate = async () => {
    setShowConfirm(false);
    setGenerating(true);
    setGeneratedText(null);
    try {
      const result = await generateReportSection(await generationParams());
      setGeneratedText(result);
    } catch (err) {
      console.error("AI generation failed:", err);
      alert(err?.response?.data?.error || "Tekstin generointi epäonnistui.");
    } finally {
      setGenerating(false);
    }
  };

  const handleUseGenerated = () => {
    if (!generatedText) return;
    setInjectContent(generatedText);
    setGeneratedText(null);
  };

  // Tags linked to this block — its own explicit field now, not parsed
  // out of the hint text.
  const activeTags = data?.valmislauseTags || [];

  // A tag's valmislause options are alternatives, not independent inserts —
  // picking one from a tag that already has an active choice swaps it out
  // (removes the old paragraph, inserts the new one); clicking the active
  // one again just removes it. `data.valmislauseActive` tracks the one
  // active choice per tag ({ [tag]: { id, text } }, text = the exact
  // resolved string as inserted, needed to find-and-remove that paragraph
  // later). Written onto dataRef.current synchronously (same pattern the
  // old InjectContentPlugin onDone used) so handleChange's own save, which
  // fires synchronously inside the editor update triggered right after,
  // spreads this change instead of a stale dataRef.
  const handleValmislauseChoice = (tag, item) => {
    const resolvedText = resolveMuuttujat(item.teksti, rakennusData, mergedRakennusData);
    const current = dataRef.current?.valmislauseActive?.[tag];
    const nextActive = { ...(dataRef.current?.valmislauseActive || {}) };
    let removeText = null;
    let insertText = null;
    if (current?.id === item.id) {
      removeText = current.text;
      delete nextActive[tag];
    } else {
      if (current) removeText = current.text;
      insertText = resolvedText;
      nextActive[tag] = { id: item.id, text: resolvedText };
    }
    const newData = { ...dataRef.current, valmislauseActive: nextActive };
    dataRef.current = newData;
    onChange?.(newData);
    setInjectMode('append');
    setRemoveInjectContent(removeText);
    setInjectContent(insertText);
  };

  return (
    <div>
      {/* UI-only hint field — not included in PDF */}
      <HintInput hint={data?.hint || ""} onBlur={hint => onChange?.({ ...data, hint })} />

      <div className="card shadow-sm">
        <style>{`@media (max-width: 576px) { .lexical-editable { font-size: 14px !important; } }`}</style>
        <div className="card-body p-2 p-sm-3">
          <LexicalComposer
            key={composerKey}
            initialConfig={initialConfig}
          >
            <HydrateFromData data={data} />
            <HistoryPlugin />
            <LineBreakPlugin />
            {data?.textRecommendationsEnabled !== false && (
              <TextRecommendationBanner
                reportId={report?.id}
                sectionType={activeSection?.type}
                blockId={blockId}
                blockLabel={blockPathLabel || subsectionTitle}
                onUse={text => { setInjectMode('replace'); setInjectContent(text); }}
                onInsert={text => { setInjectMode('append'); setInjectContent(text); }}
              />
            )}
            <Toolbar
              usePastePlugin={usePastePlugin}
              setUsePastePlugin={setUsePastePlugin}
              showGenerate={!!promptKey && canAiGenerate}
              promptTagControl={canAiGenerate ? (
                <PromptTagSelect
                  value={promptKey}
                  reportType={templateName}
                  onChange={(key) => onChange?.({ ...dataRef.current, llmPrompt: key || undefined })}
                />
              ) : null}
              generating={generating}
              onGenerateClick={() => setShowConfirm(true)}
            />

            {/* Confirm dialog — inline below toolbar */}
            {canAiGenerate && showConfirm && (
              <div className="border rounded p-2 bg-light mb-2 d-flex flex-column gap-1" style={{ fontSize: "13px" }}>
                <div>
                  <span className="text-muted me-1">Prompti:</span>
                  <strong>{promptKey}</strong>
                </div>
                <div>
                  <span className="text-muted me-1">Osio:</span>
                  <strong>{activeSection?.type}</strong>
                </div>
                {subsectionTitle && (
                  <div>
                    <span className="text-muted me-1">Aliosio:</span>
                    <strong>{subsectionTitle}</strong>
                  </div>
                )}
                <div className="text-muted" style={{ fontSize: "12px" }}>
                  Generoitu teksti näytetään tekstikentän alla. Voit hyväksyä tai hylätä sen.
                </div>
                <div className="d-flex gap-2 mt-1">
                  <button className="btn btn-sm btn-success" onClick={handleAiGenerate}>✨ Generoi</button>
                  <button className="btn btn-sm btn-outline-primary" onClick={handleShowPrompt}>
                    <i className="bi bi-eye me-1" />Näytä koko prompti
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowConfirm(false)}>Peru</button>
                </div>
              </div>
            )}

            <PromptPreviewModal
              show={preview.show}
              loading={preview.loading}
              error={preview.error}
              preview={preview.data}
              onHide={() => setPreview(p => ({ ...p, show: false }))}
            />

            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className="form-control lexical-editable"
                  style={{ minHeight: "120px", fontSize: "17px", lineHeight: "1.6" }}
                  lang="fi"
                />
              }
              placeholder={<div className="text-muted" />}
            />
            <PdfPasteFixPlugin enabled={usePastePlugin} />
            <InjectContentPlugin
              content={injectContent}
              removeContent={removeInjectContent}
              mode={injectMode}
              onDone={() => {
                // OnChangePlugin's handleChange already fires synchronously
                // as part of the same editor.update() above and saves the
                // correct full text/html/lexicalState via dataRef.current —
                // this used to also write `value: injectContent` here,
                // which in append mode is only the fragment just inserted,
                // silently overwriting handleChange's correct save with
                // just that fragment. Only reset the trigger state here.
                setInjectContent(null);
                setRemoveInjectContent(null);
                setInjectMode('replace');
              }}
            />
            <ListPlugin />
            <CheckListRegistrar />
            <TablePlugin />
            <OnChangePlugin onChange={handleChange} />
          </LexicalComposer>
        </div>
      </div>

      {/* Valmislause-tagit — alla kenttää, ei yläpuolella. "+ Valmislause"
          avaa modaalin, jossa valmislauseet selataan ja lisätään.
          Admin-only for now — same restriction as the standalone
          /valmislauseet page (App.js's AdminRoute); drop this check once
          the feature is ready for non-admins. */}
      {localUser?.rooli === 'admin' && (
        <div className="mt-2">
          <ValmislauseTagsPicker
            tags={activeTags}
            onChange={tags => onChange?.({ ...data, valmislauseTags: tags })}
            reportType={report?.type}
            sectionType={activeSection?.type}
            onInsert={text => { setInjectMode('append'); setInjectContent(text); }}
          />
          {activeTags.map(tag => (
            <ValmislauseInlineList key={tag} tag={tag} onChoose={item => handleValmislauseChoice(tag, item)} />
          ))}
        </div>
      )}


      {/* Table captions — one per table found in the HTML */}
      {(() => {
        const count = (data?.html || '').match(/<table/gi)?.length || 0;
        if (!count) return null;
        return (
          <div className="mt-2 px-1">
            {Array.from({ length: count }, (_, i) => (
              <div key={i} className="d-flex align-items-center gap-2 mb-1">
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#495057', whiteSpace: 'nowrap', minWidth: 80 }}>
                  Taulukko {(tableStartNumber ?? 1) + i}.
                </span>
                <CaptionInput
                  value={(data?.tableCaptions || [])[i] || ''}
                  onBlur={val => {
                    const caps = Array.from({ length: count }, (_, j) => (data?.tableCaptions || [])[j] || '');
                    caps[i] = val;
                    onChange?.({ ...data, tableCaptions: caps });
                  }}
                />
              </div>
            ))}
          </div>
        );
      })()}

      {/* Generated text preview */}
      {canAiGenerate && generatedText && (
        <div className="mt-2 border rounded p-3 bg-white" style={{ fontSize: "15px" }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-500 text-muted" style={{ fontSize: "13px" }}>
              ✨ Generoitu teksti
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-success"
                onClick={handleUseGenerated}
              >
                Käytä tekstiä
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setGeneratedText(null)}
              >
                Hylkää
              </button>
            </div>
          </div>
          <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.8" }}>
            {generatedText}
          </div>
        </div>
      )}
    </div>
  );
}